import { computed, makeObservable } from 'mobx'

import ShiftConflict, {
  Props,
  TShiftConflict,
} from 'modules/shift_management/store/internal/models/ShiftConflict'

class ShiftsConflicts {
  shift_conflicts: ShiftConflict[]

  constructor(shift_conflicts: ShiftConflict[]) {
    this.shift_conflicts = shift_conflicts

    makeObservable(this, {
      shiftIds: computed,
      withLeaves: computed,
      withRestDays: computed,
      _shiftIdsSet: computed,
      _shiftIdsWithLeavesSet: computed,
      _shiftIdsWithRestDaysSet: computed,
    })
  }

  /**
   * Transforms an array of objects from a request into an array of Leave
   * objects
   */
  /**
   * @deprecated Before using this method, consider using the `buildFromGraphQL` hook since
   * we are trying to move away from using mobx-rest and use GraphQL instead.
   */
  static buildFromProps(conflicts: Props[]): ShiftsConflicts {
    const conflictObjects = conflicts.map((conflict) => new ShiftConflict(conflict))

    return new ShiftsConflicts(conflictObjects)
  }

  static buildFromGraphQL(conflicts: TShiftConflict[]): ShiftsConflicts {
    const conflictObjects = conflicts.map((conflict) => ShiftConflict.buildFromGraphQL(conflict))

    return new ShiftsConflicts(conflictObjects)
  }

  get shiftIds(): number[] {
    return this.shift_conflicts.map((sc) => sc.id)
  }

  get withLeaves(): ShiftConflict[] {
    return this.shift_conflicts.filter((sc) => sc.hasLeaveConflict)
  }

  get withRestDays(): ShiftConflict[] {
    return this.shift_conflicts.filter((sc) => sc.hasRestDayConflict)
  }

  get _shiftIdsSet(): Set<number> {
    return new Set(this.shiftIds)
  }

  get _shiftIdsWithLeavesSet(): Set<number> {
    return new Set(this.withLeaves.map((sc) => sc.id))
  }

  get _shiftIdsWithRestDaysSet(): Set<number> {
    return new Set(this.withRestDays.map((sc) => sc.id))
  }

  has(shiftId: number): boolean {
    return this._shiftIdsSet.has(shiftId)
  }

  hasLeaveConflictByShiftId(shiftId: number): boolean {
    return this._shiftIdsWithLeavesSet.has(shiftId)
  }

  hasRestDayConflictByShiftId(shiftId: number): boolean {
    return this._shiftIdsWithRestDaysSet.has(shiftId)
  }
}

export default ShiftsConflicts
